import React, { useState, useEffect } from 'react';
import axios from 'axios';

const MIN_VEHICLE_LINES = 2;

const NewBidForm = ({
    onBidCreated,
    currentUserFullName,
    mode = 'create',
    bidId = null,
    onCancel
}) => {
    // ============= GETTING USER DATA FROM LOCAL STORAGE =============
    const userType = localStorage.getItem("usertype");     
    const loggedInUserId = localStorage.getItem("id") || localStorage.getItem("user_id");
    // =================================================================

    const [formData, setFormData] = useState({
        bid_date: '',
        bid_amount: '',
        bid_region: '',
        transmission: 'Any',
        drive_type: 'Any',
        reverse_option: 'Yes',
        shipment_type: 'Container',
        required_units: '1',
        reporter: currentUserFullName || 'Administrator',
        assigned_to_type: 'customize'
    });

    const [vehicleLines, setVehicleLines] = useState(
        Array.from({ length: MIN_VEHICLE_LINES }, () => '')
    );

    const [groupsData, setGroupsData] = useState({ administrators: [], groups: [] });
    const [selectedGroupId, setSelectedGroupId] = useState('');
    const [permissions, setPermissions] = useState({});
    const [loading, setLoading] = useState(false);
    const [loadingBid, setLoadingBid] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (formData.assigned_to_type === 'customize' && groupsData.groups.length === 0) {
            fetchGroups();
        }
    }, [formData.assigned_to_type]);

    // ========== LOAD EXISTING BID (EDIT MODE) ==========
    useEffect(() => {
        if (mode === 'edit' && bidId) {
            loadBidForEdit(bidId);
        }
    }, [mode, bidId]);

    const fetchGroups = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            const res = await axios.get('https://skcapi.btcjapan.co/get_groups_with_members.php', {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data.success) {
                setGroupsData(res.data);
            }
        } catch (err) {
            console.error(err);
            alert("Failed to load groups");
        } finally {
            setLoading(false);
        }
    };

    const splitVehicleDetails = (raw) => {
        const lines = (raw || '').split('\n').map(l => l.replace(/^•\s*/, '')).filter(l => l.trim() !== '');
        while (lines.length < MIN_VEHICLE_LINES) { lines.push(''); }
        return lines;
    };

    const loadBidForEdit = async (id) => {
        setLoadingBid(true);
        try {
            const token = localStorage.getItem("token");
            const res = await axios.get(`https://skcapi.btcjapan.co/get_single_bid.php?id=${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!res.data.success) { alert("Failed to load bid data"); return; }

            const bid = res.data.data;
            setFormData({
                bid_date: bid.bid_date || '',
                bid_amount: bid.bid_amount || '',
                bid_region: bid.bid_region || '',
                transmission: bid.transmission || 'Any',
                drive_type: bid.drive_type || 'Any',
                reverse_option: bid.reverse_option || 'Yes',
                shipment_type: bid.shipment_type || 'Container',
                required_units: String(bid.required_units || '1'),
                reporter: bid.reporter || currentUserFullName || 'Administrator',
                assigned_to_type: bid.assigned_to_type || 'customize'
            });
            setVehicleLines(splitVehicleDetails(bid.vehicle_details));

            const groupsRes = await axios.get('https://skcapi.btcjapan.co/get_groups_with_members.php', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (groupsRes.data.success) {
                setGroupsData(groupsRes.data);
                if (bid.assigned_to_type === 'customize') {
                    let groupIdToSelect = bid.group_id ? String(bid.group_id) : '';
                    if (!groupIdToSelect && bid.permissions?.length > 0) {
                        const userIdsInBid = bid.permissions.map(p => Number(p.user_id));
                        let bestGroupId = null, maxMatches = 0;
                        groupsRes.data.groups.forEach(group => {
                            const memberIds = group.members.map(m => Number(m.id));
                            if (group.leader_id) memberIds.push(Number(group.leader_id));
                            const matches = userIdsInBid.filter(uid => memberIds.includes(uid)).length;
                            if (matches > maxMatches) { maxMatches = matches; bestGroupId = group.group_id; }
                        });
                        if (bestGroupId) groupIdToSelect = String(bestGroupId);
                    }
                    setSelectedGroupId(groupIdToSelect);
                    const permObj = {};
                    (bid.permissions || []).forEach(p => {
                        permObj[p.user_id] = {
                            can_view: Number(p.can_view) === 1,
                            can_edit: Number(p.can_edit) === 1,
                            can_delete: Number(p.can_delete) === 1,
                            is_auto: false
                        };
                    });
                    setPermissions(permObj);
                }
            }
        } catch (err) {
            console.error(err);
            alert("Error loading bid for edit");
        } finally {
            setLoadingBid(false);
        }
    };

    const handleVehicleLineChange = (idx, value) => {
        setVehicleLines(prev => {
            const next = [...prev];
            next[idx] = value;
            if (idx === prev.length - 1 && prev[idx].trim() === '' && value.trim() !== '') {
                next.push('');
            }
            return next;
        });
    };

    const removeVehicleLine = (idx) => {
        setVehicleLines(prev => {
            if (prev.length <= 1) return prev;
            const next = prev.filter((_, i) => i !== idx);
            while (next.length < MIN_VEHICLE_LINES) { next.push(''); }
            return next;
        });
    };

    const handleGroupChange = (e) => {
        const groupId = e.target.value;
        setSelectedGroupId(groupId);
        if (!groupId) { setPermissions({}); return; }

        const group = groupsData.groups.find(g => g.group_id == groupId);
        if (!group) return;

        const newPermissions = {};
        groupsData.administrators.forEach(admin => {
            newPermissions[admin.id] = { can_view: true, can_edit: true, can_delete: true, is_auto: true };
        });
        if (group.leader_id) {
            newPermissions[group.leader_id] = { can_view: true, can_edit: true, can_delete: true, is_auto: true };
        }

        setPermissions(prev => {
            const merged = { ...newPermissions };
            Object.keys(prev).forEach(uid => {
                if (!merged[uid] && !prev[uid].is_auto) { merged[uid] = prev[uid]; }
            });
            return merged;
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (name === 'assigned_to_type' && value === 'customize') {
            setSelectedGroupId(''); setPermissions({});
        }
    };

    const handlePermissionChange = (userId, permType, checked) => {
        setPermissions(prev => {
            const current = prev[userId] || { can_view: false, can_edit: false, can_delete: false };
            return { ...prev, [userId]: { ...current, [permType]: checked } };
        });
    };

    const toggleMember = (userId, checked) => {
        if (checked) {
            setPermissions(prev => ({
                ...prev,
                [userId]: { can_view: true, can_edit: false, can_delete: false, is_auto: false }
            }));
        } else {
            setPermissions(prev => {
                const copy = { ...prev };
                if (copy[userId]?.is_auto) return prev;
                delete copy[userId];
                return copy;
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const vehicle_details = vehicleLines.map(l => l.trim()).filter(l => l !== '').join('\n');
        if (vehicle_details === '') {
            alert("Please enter at least one vehicle's details.");
            return;
        }

        setSaving(true);

        try {
            const token = localStorage.getItem("token");

            let permissionsPayload = [];
            if (formData.assigned_to_type === 'customize') {
                if (!selectedGroupId) { alert("Please select a group"); setSaving(false); return; }
                permissionsPayload = Object.entries(permissions).map(([userId, perms]) => ({
                    user_id: parseInt(userId),
                    can_view: perms.can_view ? 1 : 0,
                    can_edit: perms.can_edit ? 1 : 0,
                    can_delete: perms.can_delete ? 1 : 0
                }));
                if (permissionsPayload.length === 0) { alert("No users selected"); setSaving(false); return; }
            }

            const payload = {
                ...formData,
                vehicle_details,
                bid_amount: parseFloat(formData.bid_amount) || 0.00,
                permissions: permissionsPayload,
                group_id: selectedGroupId ? parseInt(selectedGroupId, 10) : 0
            };

            let response;
            if (mode === 'edit') {
                payload.id = bidId;
                response = await axios.post('https://skcapi.btcjapan.co/update_bid.php', payload, {
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
                });
            } else {
                response = await axios.post('https://skcapi.btcjapan.co/bids.php', payload, {
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
                });
            }

            if (response.status === 201 || response.status === 200 || response.data.success) {
                alert(mode === 'edit' ? 'Bid updated successfully!' : 'Bid created successfully!');
                if (onBidCreated) onBidCreated();
            }
        } catch (err) {
            console.error(err);
            alert(`Error: ${err.response?.data?.error || err.message}`);
        } finally {
            setSaving(false);
        }
    };

    // ==================================================================
    // THE 100% CORRECT FRONTEND DROPDOWN FILTER LOGIC
    // ==================================================================
    let displayGroups = [];
    
    // 1. ADMINISTRATOR: Sees ALL groups
    if (userType === 'administrator') {
        displayGroups = groupsData.groups;
    } 
    // 2. LEADER: Sees ONLY the group where they are the leader
    else if (userType === 'leader') {
        displayGroups = groupsData.groups.filter(g => String(g.leader_id) === String(loggedInUserId));
    } 
    // 3. MEMBER: Sees ONLY the group where they are a member
    else if (userType === 'member') {
        displayGroups = groupsData.groups.filter(group => 
            group.members.some(member => String(member.id) === String(loggedInUserId))
        );
    }

    const selectedGroup = displayGroups.find(g => g.group_id == selectedGroupId);

    // Auto-enable the logged-in member if they are a member and the group matches
    useEffect(() => {
        if (userType === 'member' && selectedGroup) {
            const isMemberInGroup = selectedGroup.members.some(m => String(m.id) === String(loggedInUserId));
            if (isMemberInGroup && !permissions[loggedInUserId]) {
                // Auto-select the logged-in member and lock it!
                setPermissions(prev => ({
                    ...prev,
                    [loggedInUserId]: { can_view: true, can_edit: false, can_delete: false, is_auto: true }
                }));
            }
        }
    }, [selectedGroup, loggedInUserId, userType]);

    const selectedGroupName = displayGroups.find(g => g.group_id == selectedGroupId);

    useEffect(() => {
        // We use 'currentSelectedGroup' here to avoid conflict with 'selectedGroup'
        if (selectedGroupName && selectedGroupName.leader_name) {
            setFormData(prev => ({
                ...prev,
                reporter: selectedGroupName.leader_name
            }));
        }
    }, [selectedGroupName]);

    if (loadingBid) {
        return <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Loading bid data...</div>;
    }

        return (
        <div className={`new-bid-form-container ${mode === 'edit' ? 'sale-order-drawer-form' : ''}`}>
            <form onSubmit={handleSubmit} className="sale-order-form">
                
                {/* ===== HERO SECTION ===== */}
                <div className="bid-form-hero">
                    <div>
                        <div className="bid-form-eyebrow">
                            <span className="eyebrow-dot"></span>
                            NEW BID
                        </div>
                        <h2>Create New Bid</h2>
                        <p>Fill in the bid details below.</p>
                    </div>
                    <div className="bid-form-hero-actions">
                        <button type="submit" className="btn-blue hero-save-btn">{mode === 'edit' ? 'Update Bid' : 'Save Bid'}</button>
                    </div>
                </div>

                {/* ===== BID DETAILS ===== */}
                <div className="form-section-card">
                    <div className="section-card-heading compact">
                        <div className="section-title-wrap">
                            <div className="section-icon">💰</div>
                            <div>
                                <h3>Bid Details</h3>
                                <p>Provide the necessary information for this bid.</p>
                            </div>
                        </div>
                    </div>
                    
                    <div className="form-fields-grid form-fields-grid-3">
                        <div className="form-group">
                            <label>Date <span>*</span></label>
                            <input type="date" name="bid_date" value={formData.bid_date} onChange={handleChange} required />
                        </div>
                        <div className="form-group">
                            <label>Bid Amount <span>*</span></label>
                            <input type="number" step="0.01" name="bid_amount" value={formData.bid_amount} onChange={handleChange} required placeholder="e.g. 700000.00" />
                        </div>
                        <div className="form-group">
                            <label>Region <span>*</span></label>
                            <select
                                style={{ width: '260px' }}
                                name="bid_region"
                                value={formData.bid_region}
                                onChange={handleChange}
                                required
                                >
                                <option value="">-- Select Country --</option>
                                <option value="Afghanistan">Afghanistan</option>
                                <option value="Albania">Albania</option>
                                <option value="Algeria">Algeria</option>
                                <option value="Andorra">Andorra</option>
                                <option value="Angola">Angola</option>
                                <option value="Antigua and Barbuda">Antigua and Barbuda</option>
                                <option value="Argentina">Argentina</option>
                                <option value="Armenia">Armenia</option>
                                <option value="Australia">Australia</option>
                                <option value="Austria">Austria</option>
                                <option value="Azerbaijan">Azerbaijan</option>
                                <option value="Bahamas">Bahamas</option>
                                <option value="Bahrain">Bahrain</option>
                                <option value="Bangladesh">Bangladesh</option>
                                <option value="Barbados">Barbados</option>
                                <option value="Belarus">Belarus</option>
                                <option value="Belgium">Belgium</option>
                                <option value="Belize">Belize</option>
                                <option value="Benin">Benin</option>
                                <option value="Bhutan">Bhutan</option>
                                <option value="Bolivia">Bolivia</option>
                                <option value="Bosnia and Herzegovina">Bosnia and Herzegovina</option>
                                <option value="Botswana">Botswana</option>
                                <option value="Brazil">Brazil</option>
                                <option value="Brunei">Brunei</option>
                                <option value="Bulgaria">Bulgaria</option>
                                <option value="Burkina Faso">Burkina Faso</option>
                                <option value="Burundi">Burundi</option>
                                <option value="Cabo Verde">Cabo Verde</option>
                                <option value="Cambodia">Cambodia</option>
                                <option value="Cameroon">Cameroon</option>
                                <option value="Canada">Canada</option>
                                <option value="Central African Republic">Central African Republic</option>
                                <option value="Chad">Chad</option>
                                <option value="Chile">Chile</option>
                                <option value="China">China</option>
                                <option value="Colombia">Colombia</option>
                                <option value="Comoros">Comoros</option>
                                <option value="Congo (Congo-Brazzaville)">Congo (Congo-Brazzaville)</option>
                                <option value="Costa Rica">Costa Rica</option>
                                <option value="Croatia">Croatia</option>
                                <option value="Cuba">Cuba</option>
                                <option value="Cyprus">Cyprus</option>
                                <option value="Czech Republic">Czech Republic</option>
                                <option value="Democratic Republic of the Congo">Democratic Republic of the Congo</option>
                                <option value="Denmark">Denmark</option>
                                <option value="Djibouti">Djibouti</option>
                                <option value="Dominica">Dominica</option>
                                <option value="Dominican Republic">Dominican Republic</option>
                                <option value="Ecuador">Ecuador</option>
                                <option value="Egypt">Egypt</option>
                                <option value="El Salvador">El Salvador</option>
                                <option value="Equatorial Guinea">Equatorial Guinea</option>
                                <option value="Eritrea">Eritrea</option>
                                <option value="Estonia">Estonia</option>
                                <option value="Eswatini">Eswatini</option>
                                <option value="Ethiopia">Ethiopia</option>
                                <option value="Fiji">Fiji</option>
                                <option value="Finland">Finland</option>
                                <option value="France">France</option>
                                <option value="Gabon">Gabon</option>
                                <option value="Gambia">Gambia</option>
                                <option value="Georgia">Georgia</option>
                                <option value="Germany">Germany</option>
                                <option value="Ghana">Ghana</option>
                                <option value="Greece">Greece</option>
                                <option value="Grenada">Grenada</option>
                                <option value="Guatemala">Guatemala</option>
                                <option value="Guinea">Guinea</option>
                                <option value="Guinea-Bissau">Guinea-Bissau</option>
                                <option value="Guyana">Guyana</option>
                                <option value="Haiti">Haiti</option>
                                <option value="Honduras">Honduras</option>
                                <option value="Hungary">Hungary</option>
                                <option value="Iceland">Iceland</option>
                                <option value="India">India</option>
                                <option value="Indonesia">Indonesia</option>
                                <option value="Iran">Iran</option>
                                <option value="Iraq">Iraq</option>
                                <option value="Ireland">Ireland</option>
                                <option value="Israel">Israel</option>
                                <option value="Italy">Italy</option>
                                <option value="Ivory Coast">Ivory Coast</option>
                                <option value="Jamaica">Jamaica</option>
                                <option value="Japan">Japan</option>
                                <option value="Jordan">Jordan</option>
                                <option value="Kazakhstan">Kazakhstan</option>
                                <option value="Kenya">Kenya</option>
                                <option value="Kiribati">Kiribati</option>
                                <option value="Kuwait">Kuwait</option>
                                <option value="Kyrgyzstan">Kyrgyzstan</option>
                                <option value="Laos">Laos</option>
                                <option value="Latvia">Latvia</option>
                                <option value="Lebanon">Lebanon</option>
                                <option value="Lesotho">Lesotho</option>
                                <option value="Liberia">Liberia</option>
                                <option value="Libya">Libya</option>
                                <option value="Liechtenstein">Liechtenstein</option>
                                <option value="Lithuania">Lithuania</option>
                                <option value="Luxembourg">Luxembourg</option>
                                <option value="Madagascar">Madagascar</option>
                                <option value="Malawi">Malawi</option>
                                <option value="Malaysia">Malaysia</option>
                                <option value="Maldives">Maldives</option>
                                <option value="Mali">Mali</option>
                                <option value="Malta">Malta</option>
                                <option value="Marshall Islands">Marshall Islands</option>
                                <option value="Mauritania">Mauritania</option>
                                <option value="Mauritius">Mauritius</option>
                                <option value="Mexico">Mexico</option>
                                <option value="Micronesia">Micronesia</option>
                                <option value="Moldova">Moldova</option>
                                <option value="Monaco">Monaco</option>
                                <option value="Mongolia">Mongolia</option>
                                <option value="Montenegro">Montenegro</option>
                                <option value="Morocco">Morocco</option>
                                <option value="Mozambique">Mozambique</option>
                                <option value="Myanmar">Myanmar</option>
                                <option value="Namibia">Namibia</option>
                                <option value="Nauru">Nauru</option>
                                <option value="Nepal">Nepal</option>
                                <option value="Netherlands">Netherlands</option>
                                <option value="New Zealand">New Zealand</option>
                                <option value="Nicaragua">Nicaragua</option>
                                <option value="Niger">Niger</option>
                                <option value="Nigeria">Nigeria</option>
                                <option value="North Korea">North Korea</option>
                                <option value="North Macedonia">North Macedonia</option>
                                <option value="Norway">Norway</option>
                                <option value="Oman">Oman</option>
                                <option value="Pakistan">Pakistan</option>
                                <option value="Palau">Palau</option>
                                <option value="Palestine">Palestine</option>
                                <option value="Panama">Panama</option>
                                <option value="Papua New Guinea">Papua New Guinea</option>
                                <option value="Paraguay">Paraguay</option>
                                <option value="Peru">Peru</option>
                                <option value="Philippines">Philippines</option>
                                <option value="Poland">Poland</option>
                                <option value="Portugal">Portugal</option>
                                <option value="Qatar">Qatar</option>
                                <option value="Romania">Romania</option>
                                <option value="Russia">Russia</option>
                                <option value="Rwanda">Rwanda</option>
                                <option value="Saint Kitts and Nevis">Saint Kitts and Nevis</option>
                                <option value="Saint Lucia">Saint Lucia</option>
                                <option value="Saint Vincent and the Grenadines">Saint Vincent and the Grenadines</option>
                                <option value="Samoa">Samoa</option>
                                <option value="San Marino">San Marino</option>
                                <option value="Sao Tome and Principe">Sao Tome and Principe</option>
                                <option value="Saudi Arabia">Saudi Arabia</option>
                                <option value="Senegal">Senegal</option>
                                <option value="Serbia">Serbia</option>
                                <option value="Seychelles">Seychelles</option>
                                <option value="Sierra Leone">Sierra Leone</option>
                                <option value="Singapore">Singapore</option>
                                <option value="Slovakia">Slovakia</option>
                                <option value="Slovenia">Slovenia</option>
                                <option value="Solomon Islands">Solomon Islands</option>
                                <option value="Somalia">Somalia</option>
                                <option value="South Africa">South Africa</option>
                                <option value="South Korea">South Korea</option>
                                <option value="South Sudan">South Sudan</option>
                                <option value="Spain">Spain</option>
                                <option value="Sri Lanka">Sri Lanka</option>
                                <option value="Sudan">Sudan</option>
                                <option value="Suriname">Suriname</option>
                                <option value="Sweden">Sweden</option>
                                <option value="Switzerland">Switzerland</option>
                                <option value="Syria">Syria</option>
                                <option value="Taiwan">Taiwan</option>
                                <option value="Tajikistan">Tajikistan</option>
                                <option value="Tanzania">Tanzania</option>
                                <option value="Thailand">Thailand</option>
                                <option value="Timor-Leste">Timor-Leste</option>
                                <option value="Togo">Togo</option>
                                <option value="Tonga">Tonga</option>
                                <option value="Trinidad and Tobago">Trinidad and Tobago</option>
                                <option value="Tunisia">Tunisia</option>
                                <option value="Turkey">Turkey</option>
                                <option value="Turkmenistan">Turkmenistan</option>
                                <option value="Tuvalu">Tuvalu</option>
                                <option value="Uganda">Uganda</option>
                                <option value="Ukraine">Ukraine</option>
                                <option value="United Arab Emirates">United Arab Emirates</option>
                                <option value="United Kingdom">United Kingdom</option>
                                <option value="United States">United States</option>
                                <option value="Uruguay">Uruguay</option>
                                <option value="Uzbekistan">Uzbekistan</option>
                                <option value="Vanuatu">Vanuatu</option>
                                <option value="Vatican City">Vatican City</option>
                                <option value="Venezuela">Venezuela</option>
                                <option value="Vietnam">Vietnam</option>
                                <option value="Yemen">Yemen</option>
                                <option value="Zambia">Zambia</option>
                                <option value="Zimbabwe">Zimbabwe</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Required Transmission</label>
                            <select name="transmission" value={formData.transmission} onChange={handleChange}><option>Any</option><option>Auto</option><option>Manual</option></select>
                        </div>
                        <div className="form-group">
                            <label>Drive</label>
                            <select name="drive_type" value={formData.drive_type} onChange={handleChange}><option>Any</option><option>2WD</option><option>4WD</option></select>
                        </div>
                        <div className="form-group">
                            <label>Reverse</label>
                            <select name="reverse_option" value={formData.reverse_option} onChange={handleChange}><option>Yes</option><option>No</option></select>
                        </div>
                        <div className="form-group">
                            <label>Shipment</label>
                            <select name="shipment_type" value={formData.shipment_type} onChange={handleChange}><option>Container</option><option>RORO</option></select>
                        </div>
                        <div className="form-group">
                            <label>Required</label>
                            <select name="required_units" value={formData.required_units} onChange={handleChange}><option>1</option><option>2</option><option>3</option></select>
                        </div>
                        <div className="form-group">
                            <label>Reporter <span>*</span></label>
                            <input type="text" name="reporter" value={formData.reporter} readOnly={true} />
                        </div>
                    </div>
                </div>

                {/* ===== VEHICLE DETAILS ===== */}
                <div className="form-section-card">
                    <div className="section-card-heading compact">
                        <div className="section-title-wrap">
                            <div className="section-icon">🚗</div>
                            <div>
                                <h3>Vehicle Details</h3>
                                <p>Enter one vehicle per line.</p>
                            </div>
                        </div>
                    </div>
                    <div>
                        {vehicleLines.map((line, idx) => (
                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                <input type="text" value={line} onChange={(e) => handleVehicleLineChange(idx, e.target.value)} placeholder="Lot > Date > Auction > Maker > Year > Other" style={{ flex: 1, padding: '10px', border: '1px solid #dce2eb', borderRadius: '10px' }} />
                                {vehicleLines.length > 1 && (<button type="button" onClick={() => removeVehicleLine(idx)} style={{ width: 30, height: 30, border: '1px solid #f0d9df', borderRadius: '8px', background: '#fff5f6', color: '#d84d65', cursor: 'pointer' }}>×</button>)}
                            </div>
                        ))}
                        <small style={{ color: '#b3b3b3', display: 'block', marginTop: '4px' }}>Enter one vehicle per line. A new line appears automatically as you type.<br />Example: 1237&gt;28.03.2026&gt;ARAI OYAMA&gt;MITSUBISHI CANTER&gt;2004&gt;other</small>     
                    </div>
                </div>

                {/* ===== PERMISSION ACCESS ===== */}
                <div className="form-section-card permission-section-card">
                    <div className="section-card-heading">
                        <div className="section-title-wrap">
                            <div className="section-icon">🔐</div>
                            <div>
                                <h3>Permission Access</h3>
                                <p>Assign this bid to specific users or groups.</p>
                            </div>
                        </div>
                        <div className="assigned-radio-group">
                            <label className="assigned-radio-option permission-choice">
                                <input type="radio" name="assigned_to_type" value="customize" checked={formData.assigned_to_type === 'customize'} onChange={handleChange} />
                                <span><strong>Customize Permission</strong><small>Assign to group</small></span>
                            </label>
                        </div>
                    </div>

                    {formData.assigned_to_type === 'customize' && (
                        <div className="permission-panel">
                            {loading ? <p className="permission-panel-loading">Loading groups...</p> : (
                                <>
                                    <div className="group-select-block">
                                        <label>Select Group <span>*</span></label>
                                        <select value={selectedGroupId} onChange={handleGroupChange}>
                                            <option value="">-- Select a Group --</option>
                                            {displayGroups.map(g => <option key={g.group_id} value={g.group_id}>{g.group_name}</option>)}
                                        </select>
                                        {displayGroups.length === 0 && <p className="group-empty-msg">No groups available for your account.</p>}
                                    </div>

                                    {selectedGroup && (
                                        <div className="permission-scroll">
                                            <div className="role-block">
                                                <p className="role-block-title admin">Administrators <span>(Auto)</span></p>
                                                {groupsData.administrators.map(admin => (
                                                    <div key={admin.id} className="role-card admin">
                                                        <strong>{admin.fname} {admin.lname}</strong>
                                                        <span className="role-card-tag">(Administrator)</span>
                                                        <div className="role-card-perms">✓ Can View &nbsp; ✓ Can Edit &nbsp; ✓ Can Delete</div>
                                                    </div>
                                                ))}
                                            </div>

                                            <div className="role-block">
                                                <p className="role-block-title leader">Group Leader <span>(Auto)</span></p>
                                                <div className="role-card leader">
                                                    <strong>{selectedGroup.leader_name}</strong>
                                                    <span className="role-card-tag">(Leader)</span>
                                                    <div className="role-card-perms">✓ Can View &nbsp; ✓ Can Edit &nbsp; ✓ Can Delete</div>
                                                </div>
                                            </div>

                                            <div className="role-block">
                                                <p className="role-block-title member">Group Members</p>
                                                {selectedGroup.members.filter(m => m.id != selectedGroup.leader_id).map(member => {
                                                    const isSelected = !!permissions[member.id];
                                                    const isSelf = String(member.id) === String(loggedInUserId) && userType === 'member';
                                                    const isDisabled = userType === 'member' || isSelf;
                                                    return (
                                                        <div key={member.id} className={`member-card ${isSelected ? 'selected' : ''}`}>
                                                            <label className="member-card-label">
                                                                <input type="checkbox" checked={isSelected || isSelf} disabled={isDisabled} onChange={(e) => toggleMember(member.id, e.target.checked)} />
                                                                {member.fname} {member.lname} {isSelf && <span className="member-self-tag">(You)</span>}
                                                                <span className="member-role-tag">({member.usertype})</span>
                                                            </label>
                                                            {isSelected && (
                                                                <div className="member-permissions-row">
                                                                    <label><input type="checkbox" checked={!!permissions[member.id]?.can_view} onChange={(e) => handlePermissionChange(member.id, 'can_view', e.target.checked)} /> Can View</label>
                                                                    <label><input type="checkbox" checked={!!permissions[member.id]?.can_edit} onChange={(e) => handlePermissionChange(member.id, 'can_edit', e.target.checked)} /> Can Edit</label>
                                                                    <label><input type="checkbox" checked={!!permissions[member.id]?.can_delete} onChange={(e) => handlePermissionChange(member.id, 'can_delete', e.target.checked)} /> Can Delete</label>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>

                <div className="form-submit-row">
                    {mode === 'edit' && <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>}
                    <button type="submit" className="btn-blue final-save-btn" disabled={saving}>{saving ? 'Saving...' : (mode === 'edit' ? 'Update Bid' : 'Save Bid')}</button>
                </div>
            </form>
        </div>
    );
};

export default NewBidForm;